import { useCallback } from 'react'

import { TypeGuards } from '@services/type-guards'
import {
  useWireBankAccountLazyQuery,
  usePixBankAccountLazyQuery,
  useCbitBankAccountLazyQuery,
  useSignetBankAccountLazyQuery,
  useXpayBankAccountLazyQuery,
  useRtpBankAccountLazyQuery,
  useSenBankAccountLazyQuery,
  useCubixBankAccountLazyQuery,
} from '@shared/graphql'

import type { BankAccount } from '@shared/graphql'

export const useGetBankAccounts = () => {
  const [getWireBankAccount, { loading: wireLoading, error: wireError }] =
    useWireBankAccountLazyQuery()
  const [getPixBankAccount, { loading: pixLoading, error: pixError }] =
    usePixBankAccountLazyQuery()
  const [getCbitBankAccount, { loading: cbitLoading, error: cbitError }] =
    useCbitBankAccountLazyQuery()
  const [getSignetBankAccount, { loading: signetLoading, error: signetError }] =
    useSignetBankAccountLazyQuery()
  const [getXpayBankAccount, { loading: xpayLoading, error: xpayError }] =
    useXpayBankAccountLazyQuery()
  const [getRtpBankAccount, { loading: rtpLoading, error: rtpError }] =
    useRtpBankAccountLazyQuery()
  const [getSenBankAccount, { loading: senLoading, error: senError }] =
    useSenBankAccountLazyQuery()
  const [getCubixBankAccount, { loading: cubixLoading, error: cubixError }] =
    useCubixBankAccountLazyQuery()

  // Combine all loading states
  const loading =
    wireLoading ||
    pixLoading ||
    cbitLoading ||
    signetLoading ||
    xpayLoading ||
    rtpLoading ||
    senLoading ||
    cubixLoading

  // Combine all error states
  const error =
    wireError ||
    pixError ||
    cbitError ||
    signetError ||
    xpayError ||
    rtpError ||
    senError ||
    cubixError

  const getBankAccount = useCallback(
    async (account: BankAccount) => {
      const accountId = account.id

      if (TypeGuards.BankAccount.isWire(account)) {
        const { data } = await getWireBankAccount({
          variables: { accountId },
        })
        return data?.wireBankAccount
      }

      if (TypeGuards.BankAccount.isPix(account)) {
        const { data } = await getPixBankAccount({ variables: { accountId } })
        return data?.pixBankAccount
      }

      if (TypeGuards.BankAccount.isCbit(account)) {
        const { data } = await getCbitBankAccount({
          variables: { accountId },
        })
        return data?.cbitBankAccount
      }

      if (TypeGuards.BankAccount.isSignet(account)) {
        const { data } = await getSignetBankAccount({
          variables: { accountId },
        })
        return data?.signetBankAccount
      }

      if (TypeGuards.BankAccount.isXpay(account)) {
        const { data } = await getXpayBankAccount({
          variables: { accountId },
        })
        return data?.xpayBankAccount
      }

      if (TypeGuards.BankAccount.isRtp(account)) {
        const { data } = await getRtpBankAccount({ variables: { accountId } })
        return data?.rtpBankAccount
      }

      if (TypeGuards.BankAccount.isSen(account)) {
        const { data } = await getSenBankAccount({ variables: { accountId } })
        return data?.senBankAccount
      }

      if (TypeGuards.BankAccount.isCubix(account)) {
        const { data } = await getCubixBankAccount({
          variables: { accountId },
        })
        return data?.cubixBankAccount
      }

      return account
    },
    [
      getWireBankAccount,
      getPixBankAccount,
      getCbitBankAccount,
      getSignetBankAccount,
      getXpayBankAccount,
      getRtpBankAccount,
      getSenBankAccount,
      getCubixBankAccount,
    ],
  )

  return [
    getBankAccount,
    {
      loading,
      error,
    },
  ] as const
}
